import React, { useState, useEffect, useRef } from 'react';

interface BlushSettings {
  color?: string;
  size?: number;
  opacity?: number;
  duration?: number;
  blur?: number;
  trigger?: 'hover' | 'always';
  shape?: 'circle' | 'square';
  delay?: number;
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right';
}

export const Blush = ({     color = 'rgba(128, 177, 85, 0.8)',
  size = 300,
  blur = 70, 
  opacity = 0.2,
  duration = 0.2 }) => {
  // Extract Blush settings
  const {

    trigger = 'hover', // Default trigger type
    shape = 'circle', // Default shape
    delay = 0, // Delay in seconds
    position = 'center', // Default position
    desktop_min_height = 0,
    mobile_min_height = 0,
    padding_top_bottom_desktop = 0,
    padding_left_right_desktop = 0,
    padding_top_botton_mobile = 0,
    padding_left_right_mobile = 0
  } = {};

  // State to track hover
  const [isActive, setIsActive] = useState(trigger === 'always');
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if mobile on mount
  useEffect(() => {
    setIsMounted(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Mouse enter handler
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsActive(true);
    }
  };

  // Mouse leave handler
  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsActive(false);
    }
  };

  // Get position styles based on the position prop
  const getPositionStyle = () => {
    switch(position) {
      case 'top':
        return { left: '50%', top: '0', transform: isActive ? 'translate(-50%, -30%) scale(1)' : 'translate(-50%, -30%) scale(0.8)' };
      case 'bottom':
        return { left: '50%', bottom: '0', transform: isActive ? 'translate(-50%, 30%) scale(1)' : 'translate(-50%, 30%) scale(0.8)' };
      case 'left':
        return { left: '0', top: '50%', transform: isActive ? 'translate(-30%, -50%) scale(1)' : 'translate(-30%, -50%) scale(0.8)' };
      case 'right':
        return { right: '0', top: '50%', transform: isActive ? 'translate(30%, -50%) scale(1)' : 'translate(30%, -50%) scale(0.8)' };
      case 'center':
      default:
        return { left: '50%', top: '50%', transform: isActive ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.8)' };
    }
  };

  // Calculate the styles for the blush effect
  const blushStyle = {
    position: 'absolute' as const,
    pointerEvents: 'none' as const,
    width: `${size}px`,
    height: shape === 'circle' ? `${size}px` : `${size * 0.7}px`,
    borderRadius: shape === 'circle' ? '50%' : '25%',
    background: color,
    opacity: opacity,
    filter: `blur(${blur}px)`,
    transition: `opacity ${duration}s ease-in-out, transform ${duration}s ease-in-out`,
    transitionDelay: `${delay}s`,
    zIndex: 0,
    ...getPositionStyle()
  };

  // Container styles
  const containerStyle = {
    position: 'absolute' as const,
    top:0,
    left: 0,
    zIndex: 0,
    width: '100%',
    height: '100%',
    minHeight: isMobile ? `${mobile_min_height}px` : `${desktop_min_height}px`,
    padding: isMobile 
      ? `${padding_top_botton_mobile}px ${padding_left_right_mobile}px`
      : `${padding_top_bottom_desktop}px ${padding_left_right_desktop}px`,
  };

  // Inner content style to ensure content stays above the blush effect
  const contentStyle = {
    position: 'relative' as const,
    zIndex: 1,
    width: '100%',
    height: '100%',
  };

  return (
    <div 
      ref={containerRef}
      className="blush-container"
      style={containerStyle}
      onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
      onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}
    >
      {/* The blush effect element */}
      <div className="blush-effect" style={blushStyle} />
      
    
    </div>
  );
};

export default Blush;